import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSecurityAlerts, getSecurityAlertsStats, reviewSecurityAlert, getSecurityAlertDetail, getProxiedImageUrl, getLivenessSkipReport } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from '../ui/sonner';
import { 
  ShieldAlert, AlertTriangle, MapPin, Clock, User, School, 
  ChevronLeft, RefreshCw, CheckCircle, XCircle, Eye, Filter,
  AlertCircle, Loader2, FileWarning, Smartphone, Target, Camera
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const severityConfig = {
  critical: { color: 'bg-red-600 text-white', label: 'Kritis', icon: AlertTriangle },
  high: { color: 'bg-orange-500 text-white', label: 'Tinggi', icon: AlertCircle },
  medium: { color: 'bg-yellow-500 text-white', label: 'Sedang', icon: FileWarning },
  low: { color: 'bg-blue-500 text-white', label: 'Rendah', icon: AlertCircle }
};

const alertTypeConfig = {
  fake_gps: { label: 'Fake GPS', icon: MapPin, color: 'text-red-600' },
  outside_geofence: { label: 'Luar Radius', icon: Target, color: 'text-orange-600' },
  timestamp_anomaly: { label: 'Anomali Waktu', icon: Clock, color: 'text-purple-600' },
  suspicious_accuracy: { label: 'Akurasi Mencurigakan', icon: MapPin, color: 'text-yellow-600' },
  offline_sync_delay: { label: 'Sync Terlambat', icon: Clock, color: 'text-blue-600' },
  face_not_verified: { label: 'Wajah Tidak Cocok', icon: User, color: 'text-pink-600' },
  face_not_registered: { label: 'Wajah Belum Terdaftar', icon: User, color: 'text-gray-600' },
  face_liveness_skipped: { label: 'Liveness Dilewati', icon: User, color: 'text-amber-600' },
  face_low_match: { label: 'Kemiripan Wajah Rendah', icon: User, color: 'text-orange-500' },
  liveness_skip_pattern: { label: 'Pola Skip Liveness', icon: AlertTriangle, color: 'text-red-500' },
  early_checkout: { label: 'Pulang Awal', icon: Clock, color: 'text-indigo-600' },
};

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Menunggu Review' },
  reviewed: { color: 'bg-blue-100 text-blue-800', label: 'Sudah Direview' },
  dismissed: { color: 'bg-gray-100 text-gray-800', label: 'Diabaikan' },
  confirmed: { color: 'bg-red-100 text-red-800', label: 'Dikonfirmasi Curang' }
};

const SecurityAlertsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterSeverity, setFilterSeverity] = useState('all');
  
  // Dialog state
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [alertDetail, setAlertDetail] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('reviewed');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Check if user can review (only admin_dinas)
  const canReview = user?.role === 'admin_dinas';

  // Liveness skip report state
  const [activeTab, setActiveTab] = useState('alerts');
  const [skipReport, setSkipReport] = useState(null);
  const [skipReportLoading, setSkipReportLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterStatus, filterSeverity]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSeverity !== 'all') params.severity = filterSeverity;
      
      const [alertsData, statsData] = await Promise.all([
        getSecurityAlerts(params),
        getSecurityAlertsStats()
      ]);
      
      setAlerts(alertsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error('Gagal memuat data alert');
    } finally {
      setLoading(false);
    }
  };

  const loadSkipReport = async () => {
    try {
      setSkipReportLoading(true);
      const data = await getLivenessSkipReport(30);
      setSkipReport(data);
    } catch (error) {
      console.error('Failed to load liveness skip report:', error);
      toast.error('Gagal memuat laporan liveness skip');
    } finally {
      setSkipReportLoading(false);
    }
  };

  const openAlertDetail = async (alert) => {
    setSelectedAlert(alert);
    setDetailLoading(true);
    try {
      const detail = await getSecurityAlertDetail(alert.id);
      setAlertDetail(detail);
    } catch (error) {
      console.error('Failed to load alert detail:', error);
      toast.error('Gagal memuat detail alert');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeAlertDetail = () => {
    setSelectedAlert(null);
    setAlertDetail(null);
  };

  const openReviewDialog = (alert) => {
    setSelectedAlert(alert);
    setReviewStatus('reviewed');
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReview = async () => {
    if (!selectedAlert) return;
    
    setSubmitting(true);
    try {
      await reviewSecurityAlert(selectedAlert.id, {
        status: reviewStatus,
        notes: reviewNotes
      });
      
      toast.success(`Alert berhasil di-${reviewStatus}`, {
        description: selectedAlert.guru_nama
      });
      
      setReviewDialogOpen(false);
      setSelectedAlert(null);
      loadData();
    } catch (error) {
      console.error('Failed to review alert:', error);
      toast.error('Gagal mereview alert');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'admin_dinas' && user?.role !== 'kepala_sekolah' && user?.role !== 'pimpinan' && user?.role !== 'editor') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Anda tidak memiliki akses ke halaman ini.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="security-alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950 flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-red-600" />
              Security Alerts
            </h1>
            <p className="text-gray-500">Monitor dan review absensi mencurigakan</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadData} className="border-indigo-200">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2" data-testid="security-tabs">
        <Button
          variant={activeTab === 'alerts' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('alerts')}
          className={activeTab === 'alerts' ? 'bg-indigo-900' : ''}
        >
          <ShieldAlert className="w-4 h-4 mr-2" />
          Security Alerts
        </Button>
        <Button
          variant={activeTab === 'liveness' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => { setActiveTab('liveness'); if (!skipReport) loadSkipReport(); }}
          className={activeTab === 'liveness' ? 'bg-indigo-900' : ''}
          data-testid="liveness-report-tab"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Laporan Liveness Skip
          {stats?.by_type?.liveness_skip_pattern > 0 && (
            <Badge className="ml-2 bg-red-500 text-white">{stats.by_type.liveness_skip_pattern}</Badge>
          )}
        </Button>
      </div>

      {activeTab === 'liveness' && (
        <div className="space-y-4" data-testid="liveness-skip-report">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Laporan Staff yang Sering Skip Liveness (30 Hari)
              </CardTitle>
              <CardDescription>
                Staff yang melewati verifikasi liveness saat absensi. Threshold alert: 3x skip dalam 30 hari.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {skipReportLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat laporan...
                </div>
              ) : skipReport && skipReport.data?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium">Nama</th>
                        <th className="text-left py-2 px-3 font-medium">Sekolah/Instansi</th>
                        <th className="text-center py-2 px-3 font-medium">Skip</th>
                        <th className="text-center py-2 px-3 font-medium">Total Absensi</th>
                        <th className="text-center py-2 px-3 font-medium">Rate</th>
                        <th className="text-left py-2 px-3 font-medium">Terakhir Skip</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skipReport.data.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`skip-row-${idx}`}>
                          <td className="py-2 px-3 font-medium">{row.guru_nama}</td>
                          <td className="py-2 px-3 text-gray-600">{row.sekolah_nama || row.instansi_nama || '-'}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={row.skip_count >= 5 ? 'bg-red-100 text-red-700' : row.skip_count >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}>
                              {row.skip_count}x
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-center text-gray-600">{row.total_absensi}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={row.skip_rate > 50 ? 'bg-red-100 text-red-700' : row.skip_rate > 25 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                              {row.skip_rate}%
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-gray-500 text-xs">{formatDate(row.last_skip)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
                  <p>Tidak ada staff yang skip liveness dalam 30 hari terakhir.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'alerts' && <>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.by_severity.critical}</div>
              <div className="text-sm text-red-700">Kritis</div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{stats.by_severity.high}</div>
              <div className="text-sm text-orange-700">Tinggi</div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{stats.by_severity.medium}</div>
              <div className="text-sm text-yellow-700">Sedang</div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.by_severity.low}</div>
              <div className="text-sm text-blue-700">Rendah</div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-amber-600">{stats.total_pending}</div>
              <div className="text-sm text-amber-700">Menunggu</div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-emerald-600">{stats.total_reviewed}</div>
              <div className="text-sm text-emerald-700">Direview</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-indigo-100/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="pending">Menunggu Review</SelectItem>
                <SelectItem value="reviewed">Sudah Direview</SelectItem>
                <SelectItem value="dismissed">Diabaikan</SelectItem>
                <SelectItem value="confirmed">Dikonfirmasi Curang</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Level</SelectItem>
                <SelectItem value="critical">Kritis</SelectItem>
                <SelectItem value="high">Tinggi</SelectItem>
                <SelectItem value="medium">Sedang</SelectItem>
                <SelectItem value="low">Rendah</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle>Daftar Alert ({alerts.length})</CardTitle>
          <CardDescription>Klik untuk melihat detail atau review</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShieldAlert className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Tidak ada alert untuk filter ini</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => {
                const severityConf = severityConfig[alert.severity] || severityConfig.low;
                const typeConf = alertTypeConfig[alert.alert_type] || alertTypeConfig.fake_gps;
                const statusConf = statusConfig[alert.status] || statusConfig.pending;
                const TypeIcon = typeConf.icon;
                
                return (
                  <div 
                    key={alert.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${typeConf.color} bg-opacity-10`}>
                          <TypeIcon className={`w-5 h-5 ${typeConf.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={severityConf.color}>{severityConf.label}</Badge>
                            <Badge variant="outline">{typeConf.label}</Badge>
                            <Badge className={statusConf.color}>{statusConf.label}</Badge>
                          </div>
                          <h4 className="font-semibold">{alert.guru_nama}</h4>
                          <p className="text-sm text-gray-500">{alert.sekolah_nama}</p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{alert.description}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {formatDate(alert.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openAlertDetail(alert)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detail
                        </Button>
                        {alert.status === 'pending' && canReview && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => openReviewDialog(alert)}
                            className="bg-indigo-600 hover:bg-indigo-700"
                          >
                            Review
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedAlert && !reviewDialogOpen} onOpenChange={closeAlertDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              Detail Alert
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : alertDetail && (
            <div className="space-y-4">
              {/* Guru Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" /> Informasi Guru
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Nama: <span className="font-medium">{alertDetail.guru?.nama}</span></div>
                  <div>NIP: <span className="font-medium">{alertDetail.guru?.nip || '-'}</span></div>
                  <div>Sekolah: <span className="font-medium">{alertDetail.sekolah_nama}</span></div>
                  <div>Email: <span className="font-medium">{alertDetail.guru?.email || '-'}</span></div>
                </div>
              </div>
              
              {/* Flags */}
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-4 h-4" /> Flag Keamanan
                </h4>
                <div className="space-y-2">
                  {alertDetail.details?.flags?.map((flag, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <div>
                        <span className="font-medium text-red-700">{flag.type}:</span>
                        <span className="ml-1">{flag.message}</span>
                        {flag.value && <span className="ml-1 text-gray-500">({flag.value})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Absensi Detail */}
              {alertDetail.absensi && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-700">
                    <Clock className="w-4 h-4" /> Detail Absensi
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Waktu: <span className="font-medium">{formatDate(alertDetail.absensi.waktu_absen)}</span></div>
                    <div>Tipe: <span className="font-medium">{alertDetail.absensi.tipe}</span></div>
                    <div>Status: <span className="font-medium">{alertDetail.absensi.status}</span></div>
                    <div>Device: <span className="font-medium">{alertDetail.absensi.device_type}</span></div>
                    <div>Akurasi GPS: <span className="font-medium">{alertDetail.absensi.akurasi_gps}m</span></div>
                    <div>Jarak: <span className="font-medium">{alertDetail.absensi.jarak_dari_sekolah}m</span></div>
                    <div>Dalam Radius: <span className={`font-medium ${alertDetail.absensi.dalam_radius ? 'text-green-600' : 'text-red-600'}`}>
                      {alertDetail.absensi.dalam_radius ? 'Ya' : 'Tidak'}
                    </span></div>
                    <div>GPS Verified: <span className={`font-medium ${alertDetail.absensi.gps_verified ? 'text-green-600' : 'text-red-600'}`}>
                      {alertDetail.absensi.gps_verified ? 'Ya' : 'Tidak'}
                    </span></div>
                  </div>
                </div>
              )}
              
              {/* Review History */}
              {alertDetail.reviewed_at && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" /> Status Review
                  </h4>
                  <div className="text-sm">
                    <div>Status: <Badge className={statusConfig[alertDetail.status]?.color}>{statusConfig[alertDetail.status]?.label}</Badge></div>
                    <div className="mt-1">Direview pada: {formatDate(alertDetail.reviewed_at)}</div>
                    {alertDetail.review_notes && <div className="mt-1">Catatan: {alertDetail.review_notes}</div>}
                  </div>
                </div>
              )}
              
              {/* Face Comparison Section - Show for ALL alerts with photo comparison */}
              {alertDetail.face_comparison && (
                <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-pink-700">
                    <Camera className="w-4 h-4" /> Perbandingan Foto
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Registered Face */}
                    <div className="text-center">
                      <p className="text-sm font-medium mb-2 text-gray-700">Foto Wajah Terdaftar</p>
                      {alertDetail.face_comparison.foto_referensi ? (
                        <div className="relative inline-block">
                          <img 
                            src={`data:image/jpeg;base64,${alertDetail.face_comparison.foto_referensi}`}
                            alt="Foto Referensi" 
                            className="w-36 h-36 object-cover rounded-lg border-2 border-green-400 mx-auto select-none"
                            draggable="false"
                            onContextMenu={(e) => e.preventDefault()}
                          />
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                            <Badge className="bg-green-500 text-white text-xs">Referensi</Badge>
                          </div>
                        </div>
                      ) : (
                        <div className="w-36 h-36 bg-gray-200 rounded-lg flex items-center justify-center mx-auto border-2 border-dashed border-gray-300">
                          <div className="text-center text-gray-500">
                            <User className="w-8 h-8 mx-auto mb-1 opacity-50" />
                            <span className="text-xs">Belum Terdaftar</span>
                          </div>
                        </div>
                      )}
                      {alertDetail.face_comparison.face_registered_at && (
                        <p className="text-xs text-gray-500 mt-3">
                          Didaftarkan: {formatDate(alertDetail.face_comparison.face_registered_at)}
                        </p>
                      )}
                    </div>
                    
                    {/* Attendance Face */}
                    <div className="text-center">
                      <p className="text-sm font-medium mb-2 text-gray-700">Foto Saat Absensi</p>
                      {alertDetail.face_comparison.foto_absensi ? (
                        <div className="relative inline-block">
                          <img 
                            src={getProxiedImageUrl(alertDetail.face_comparison.foto_absensi)}
                            alt="Foto Absensi" 
                            className="w-36 h-36 object-cover rounded-lg border-2 border-blue-400 mx-auto select-none"
                            draggable="false"
                            onContextMenu={(e) => e.preventDefault()}
                            onError={(e) => {
                              // Fallback if proxy fails
                              console.log('Image proxy failed, trying direct URL');
                              e.target.src = alertDetail.face_comparison.foto_absensi;
                            }}
                          />
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                            <Badge className="bg-blue-500 text-white text-xs">Absensi</Badge>
                          </div>
                        </div>
                      ) : (
                        <div className="w-36 h-36 bg-gray-200 rounded-lg flex items-center justify-center mx-auto border-2 border-dashed border-gray-300">
                          <div className="text-center text-gray-500">
                            <Camera className="w-8 h-8 mx-auto mb-1 opacity-50" />
                            <span className="text-xs">Tidak Ada Foto</span>
                          </div>
                        </div>
                      )}
                      {alertDetail.absensi?.waktu_absen && (
                        <p className="text-xs text-gray-500 mt-3">
                          Diambil: {formatDate(alertDetail.absensi.waktu_absen)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Match Score & Liveness Info */}
                  <div className="mt-4 pt-3 border-t border-pink-200">
                    <div className="flex flex-wrap justify-center gap-3">
                      {alertDetail.face_comparison.face_match_score !== undefined && alertDetail.face_comparison.face_match_score !== null && (
                        <Badge className={`${alertDetail.face_comparison.face_match_score >= 70 ? 'bg-green-100 text-green-700' : alertDetail.face_comparison.face_match_score >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          Kemiripan: {alertDetail.face_comparison.face_match_score.toFixed(1)}%
                        </Badge>
                      )}
                      {alertDetail.face_comparison.liveness_skipped && (
                        <Badge className="bg-amber-100 text-amber-700">
                          Liveness Dilewati
                        </Badge>
                      )}
                      {alertDetail.absensi?.face_verified && (
                        <Badge className="bg-green-100 text-green-700">
                          Wajah Terverifikasi
                        </Badge>
                      )}
                      {alertDetail.absensi?.face_verified_offline && (
                        <Badge className="bg-blue-100 text-blue-700">
                          Verifikasi Offline
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info for Kepala Sekolah */}
              {!canReview && alertDetail.status === 'pending' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-700">
                    <span className="font-medium">Info:</span> Hanya Admin Dinas yang dapat melakukan review terhadap alert ini.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={closeAlertDetail}>Tutup</Button>
            {alertDetail?.status === 'pending' && canReview && (
              <Button onClick={() => { closeAlertDetail(); openReviewDialog(alertDetail); }}>
                Review Alert
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Security Alert</DialogTitle>
            <DialogDescription>
              Pilih tindakan untuk alert dari {selectedAlert?.guru_nama}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Keputusan</label>
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reviewed">
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4" /> Tandai Sudah Direview
                    </span>
                  </SelectItem>
                  <SelectItem value="dismissed">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> Abaikan (False Positive)
                    </span>
                  </SelectItem>
                  <SelectItem value="confirmed">
                    <span className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-4 h-4" /> Konfirmasi Kecurangan
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Catatan (opsional)</label>
              <Textarea 
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Tambahkan catatan..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Batal</Button>
            <Button 
              onClick={handleReview} 
              disabled={submitting}
              className={reviewStatus === 'confirmed' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {reviewStatus === 'confirmed' ? 'Konfirmasi Curang' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>}
    </div>
  );
};

export default SecurityAlertsPage;
